import {
  ExclamationCircleOutlined,
  FolderOpenOutlined,
  PlusOutlined,
  SettingOutlined
} from "@ant-design/icons"
import { Button, Flex, Modal, Typography } from "antd"
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { type ConnectedProps, connect } from "react-redux"
import {
  Outlet,
  useLocation,
  useNavigate,
  useOutletContext,
  useSearchParams
} from "react-router-dom"

import { ActionTooltip } from "../../../../components/ActionTooltip.tsx"
import { SearchBar } from "../../../../components/Input/SearchBar.tsx"
import { BulkActionBar } from "../../../../components/Table/BulkActionBar.tsx"
import { neutral } from "../../../../components/widgets/tokens.ts"
import { RoutesPaths } from "../../../../constants/routes.ts"
import type { RootState } from "../../../../store.ts"
import { PageBranchFilter } from "../../../Branches/components/PageBranchFilter.tsx"
import { canFileReport, knownFolderNames } from "../../helpers/reportFolders.ts"
import {
  matchesReportSearch,
  navSpansFolders,
  newReportHref,
  reportsInNav
} from "../../helpers/reportsNav.ts"
import { useInert } from "../../hooks/useInert.ts"
import { useReportSettingsSubmit } from "../../hooks/useReportSettingsSubmit.ts"
import {
  deleteReport,
  duplicateReport,
  fetchReports,
  toggleReportFavorite
} from "../../redux/reports.thunks.ts"
import type { ReportListItem } from "../../types/types.ts"
import { ReportShareModal } from "../canvas/ReportShareModal.tsx"
import { NewFolderButton } from "../folders/NewFolderButton.tsx"
import { ReportMoveModal } from "../folders/ReportMoveModal.tsx"
import { ReportSettingsModal } from "../ReportSettingsModal.tsx"
import { type ReportsEmptyKind, ReportsEmpty } from "../ReportsEmpty.tsx"
import { ReportsList } from "../ReportsList.tsx"
import { ReportsIntro } from "./ReportsIntro.tsx"
import type { ReportsShellOutletContext } from "./ReportsShell.tsx"

const { Title, Text } = Typography

const QUICK_VIEW_TITLE_KEYS = {
  all: "reports.rail.nav.all",
  favorites: "reports.rail.nav.favorites",
  recent: "reports.rail.nav.recent",
  shared: "reports.rail.nav.shared"
} as const

const SEARCH_PARAM = "q"

const ReportsHomePage = ({
  results,
  fetching,
  folders,
  toggleFavorite,
  duplicateReport,
  deleteReport,
  getReports
}: ConnectedProps<typeof connector>) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { activeNav } = useOutletContext<ReportsShellOutletContext>()
  const [searchParams, setSearchParams] = useSearchParams()
  const search = searchParams.get(SEARCH_PARAM) ?? ""

  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [settingsTarget, setSettingsTarget] = useState<ReportListItem | null>(
    null
  )
  const [shareTarget, setShareTarget] = useState<ReportListItem | null>(null)
  const [moveTargets, setMoveTargets] = useState<ReportListItem[] | null>(null)
  const [columnsOpen, setColumnsOpen] = useState(false)
  // The search field is uncontrolled; clearing it from the empty state
  // remounts it so the box empties along with the URL.
  const [searchResetSeq, setSearchResetSeq] = useState(0)
  const handleSettingsSubmit = useReportSettingsSubmit(settingsTarget?.id)

  const folderNames = useMemo(
    () => knownFolderNames(results, folders),
    [results, folders]
  )
  const inNav = useMemo(
    () => reportsInNav(results, activeNav),
    [results, activeNav]
  )
  const visibleReports = useMemo(
    () => inNav.filter((report) => matchesReportSearch(report, search)),
    [inNav, search]
  )

  // A selection belongs to the view it was made in; a report that left the
  // list (deleted, moved out of this folder) leaves the selection too.
  const navKey = JSON.stringify(activeNav)
  useEffect(() => setSelectedIds([]), [navKey, search])
  useEffect(() => {
    const present = new Set(visibleReports.map((report) => report.id))
    setSelectedIds((ids) =>
      ids.every((id) => present.has(id))
        ? ids
        : ids.filter((id) => present.has(id))
    )
  }, [visibleReports])

  const selectedReports = useMemo(() => {
    const ids = new Set(selectedIds)
    return results.filter((report) => ids.has(report.id))
  }, [results, selectedIds])

  const setSearch = useCallback(
    (value: string | null) =>
      setSearchParams(
        (params) => {
          const next = new URLSearchParams(params)
          if (value) next.set(SEARCH_PARAM, value)
          else next.delete(SEARCH_PARAM)
          return next
        },
        { replace: true }
      ),
    [setSearchParams]
  )

  // New Report opens as a modal over this page; the control that opened it
  // gets focus back when it closes.
  const location = useLocation()
  const wizardOpen = location.pathname.startsWith(RoutesPaths.reports.create)
  // Declared before the focus hand-back below: the page has to stop being
  // inert before its button can take focus.
  const pageRef = useInert<HTMLDivElement>(wizardOpen)
  const newReportButtonRef = useRef<HTMLButtonElement>(null)
  const launcherRef = useRef<HTMLElement | null>(null)
  const wasWizardOpen = useRef(wizardOpen)
  useEffect(() => {
    if (wasWizardOpen.current && !wizardOpen) {
      const launcher = launcherRef.current
      const target =
        launcher && document.body.contains(launcher)
          ? launcher
          : newReportButtonRef.current
      target?.focus()
    }
    wasWizardOpen.current = wizardOpen
  }, [wizardOpen])

  const handleCreate = useCallback(() => {
    launcherRef.current = document.activeElement as HTMLElement | null
    navigate(newReportHref(searchParams))
  }, [navigate, searchParams])

  const handleOpen = useCallback(
    (report: ReportListItem) => navigate(RoutesPaths.reports.view(report.id)),
    [navigate]
  )

  const handleEdit = useCallback(
    (report: ReportListItem) => navigate(RoutesPaths.reports.edit(report.id)),
    [navigate]
  )

  const handleToggleFavorite = useCallback(
    (report: ReportListItem) =>
      toggleFavorite({ id: report.id, previousIsFavorite: report.is_favorite }),
    [toggleFavorite]
  )

  const handleDuplicate = useCallback(
    (report: ReportListItem) =>
      duplicateReport({
        id: report.id,
        name: report.name,
        description: report.description,
        tags: report.tags,
        visibility: report.visibility,
        folder: report.folder
      }),
    [duplicateReport]
  )

  const handleDelete = useCallback(
    (report: ReportListItem) => {
      Modal.confirm({
        title: t("reports.deleteConfirm.title"),
        content: t("reports.deleteConfirm.body", { name: report.name }),
        icon: <ExclamationCircleOutlined />,
        okText: t("reports.deleteConfirm.confirm"),
        okButtonProps: { danger: true },
        cancelText: t("reports.cancel"),
        onOk: () => deleteReport({ id: report.id })
      })
    },
    [deleteReport, t]
  )

  const handleMoveOne = useCallback(
    (report: ReportListItem) => setMoveTargets([report]),
    []
  )

  const title =
    activeNav.kind === "folder"
      ? activeNav.name
      : activeNav.kind === "unfiled"
        ? t("reports.folders.unfiled")
        : t(QUICK_VIEW_TITLE_KEYS[activeNav.view])

  const collectionEmpty = !fetching && results.length === 0
  const allReportsView = activeNav.kind === "quick" && activeNav.view === "all"

  const emptyKind: ReportsEmptyKind | null =
    fetching || visibleReports.length > 0
      ? null
      : inNav.length > 0
        ? "search"
        : activeNav.kind === "folder" || activeNav.kind === "unfiled"
          ? "folder"
          : activeNav.view === "all"
            ? "none"
            : activeNav.view

  const movableSelected = selectedReports.filter(canFileReport).length

  return (
    <>
      {/* Behind the New Report modal the list is inert: unreachable by keyboard,
        pointer and assistive technology alike, not just covered by the mask. */}
      <Flex vertical gap="middle" className="min-h-full pb-6" ref={pageRef}>
        {allReportsView && <ReportsIntro />}

        <Flex
          justify="space-between"
          align="flex-start"
          wrap="wrap"
          gap="middle"
        >
          <Flex vertical gap={2} className="min-w-0">
            <Title
              level={3}
              className="m-0 text-start"
              ellipsis={{ tooltip: title }}
            >
              {title}
            </Title>
            <Text type="secondary">
              {t("reports.reportCount", { count: inNav.length })}
            </Text>
          </Flex>
          {/* With no report at all, the empty state's "Create your first
            report" is the one primary action. */}
          {!(collectionEmpty && allReportsView) && (
            <Button
              ref={newReportButtonRef}
              type="primary"
              icon={<PlusOutlined aria-hidden />}
              onClick={handleCreate}
            >
              {t("reports.newReport")}
            </Button>
          )}
        </Flex>

        <div
          className="flex flex-col overflow-hidden rounded-xl border border-solid bg-white"
          style={{ borderColor: neutral.border }}
        >
          {!collectionEmpty && (
            <div
              role="toolbar"
              aria-label={t("reports.toolbar.label")}
              className="flex flex-wrap items-center gap-2 border-0 border-b border-solid p-3"
              style={{ borderColor: neutral.border }}
            >
              <SearchBar
                key={searchResetSeq}
                placeholder={t("reports.searchPlaceholder")}
                defaultValue={search}
                onSearch={setSearch}
                aria-label={t("reports.searchPlaceholder")}
                style={{ maxWidth: 420 }}
              />
              <PageBranchFilter page="reports" />
              <div className="ml-auto">
                <ActionTooltip title={t("reports.toolbar.tableSettings")}>
                  <Button
                    icon={<SettingOutlined aria-hidden />}
                    aria-label={t("reports.toolbar.tableSettings")}
                    onClick={() => setColumnsOpen(true)}
                  />
                </ActionTooltip>
              </div>
            </div>
          )}

          {emptyKind ? (
            <ReportsEmpty
              kind={emptyKind}
              createButtonRef={newReportButtonRef}
              onCreate={handleCreate}
              onClearSearch={() => {
                setSearch(null)
                setSearchResetSeq((seq) => seq + 1)
              }}
            />
          ) : (
            <ReportsList
              reports={visibleReports}
              loading={fetching}
              showFolder={navSpansFolders(activeNav)}
              rowSelection={{
                selectedRowKeys: selectedIds,
                onChange: (keys) => setSelectedIds(keys as string[])
              }}
              configControl={{
                open: columnsOpen,
                onOpenChange: setColumnsOpen
              }}
              onOpen={handleOpen}
              onEdit={handleEdit}
              onToggleFavorite={handleToggleFavorite}
              onSettings={setSettingsTarget}
              onMove={handleMoveOne}
              onShare={setShareTarget}
              onDuplicate={handleDuplicate}
              onDelete={handleDelete}
            />
          )}

          <BulkActionBar
            data-testid="reports-bulk-action-bar"
            selectedCount={selectedIds.length}
            selectedLabel={t("reports.bulk.selected", {
              count: selectedIds.length
            })}
            ariaLabel={t("reports.bulk.toolbarLabel")}
            actions={[
              {
                key: "move",
                label: t("reports.actions.move"),
                icon: <FolderOpenOutlined aria-hidden />,
                onClick: () => setMoveTargets(selectedReports),
                disabled: movableSelected === 0,
                disabledReason: t("reports.bulk.noneMovable")
              }
            ]}
            onClear={() => setSelectedIds([])}
          />
        </div>

        {settingsTarget && (
          <ReportSettingsModal
            report={settingsTarget}
            folderOptions={folderNames}
            onSubmit={handleSettingsSubmit}
            renderFolderAction={(setFolder) => (
              <NewFolderButton existing={folderNames} onCreated={setFolder} />
            )}
            onClose={() => setSettingsTarget(null)}
          />
        )}

        {shareTarget && (
          <ReportShareModal
            reportId={shareTarget.id}
            owner={shareTarget.owner}
            initialVisibility={shareTarget.visibility}
            onClose={() => setShareTarget(null)}
            onSaved={() => getReports({})}
          />
        )}

        {moveTargets && (
          <ReportMoveModal
            reports={moveTargets}
            folders={folderNames}
            onClose={() => setMoveTargets(null)}
            onMoved={(ids) =>
              setSelectedIds((current) =>
                current.filter((id) => !ids.includes(id))
              )
            }
          />
        )}
      </Flex>
      <Outlet />
    </>
  )
}

const mapStateToProps = (state: RootState) => ({
  results: state.reports.results,
  fetching: state.reports.fetching,
  folders: state.reports.folders
})

const mapDispatchToProps = {
  toggleFavorite: toggleReportFavorite,
  duplicateReport,
  deleteReport,
  getReports: fetchReports
}

const connector = connect(mapStateToProps, mapDispatchToProps)
export default connector(ReportsHomePage)
